/**
 * Tela da partida: tabuleiro, relogios e desfecho.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { Board } from '../components/Board.tsx';
import { AnimatePresence } from 'motion/react';

import { Desfecho, type TipoDesfecho } from '../components/Desfecho.tsx';
import { EsqueletoMesa } from '../components/Esqueleto.tsx';
import type { GameType, MatchAdapter, MatchSnapshot } from '../lib/adapter.ts';
import { GAME_LABEL, clock, money, reason } from '../lib/format.ts';
import { payoutCents, rakeCents } from '../lib/demo.ts';

export interface MatchResult {
  /** Quanto volta para o saldo virtual: pote menos rake, ou a entrada num empate. */
  readonly creditoCents: number;
  readonly venceu: boolean;
  readonly empate: boolean;
}

interface MatchScreenProps {
  readonly gameType: GameType;
  readonly entryFeeCents: number;
  /** Quem está do outro lado, para o desfecho ter rosto. */
  readonly adversario?: {
    readonly displayName: string;
    readonly avatar: string;
    readonly avatarColor: string;
  } | null;
  /** Ausente quando não há para onde voltar a jogar direto. */
  readonly onJogarDeNovo?: () => void;
  /**
   * Fabrica o adaptador da partida. A tela nao sabe — e nao precisa saber —
   * se do outro lado ha um bot no navegador ou uma pessoa.
   */
  readonly criarAdapter: () => MatchAdapter;
  /**
   * Identidade da partida. Trocar este valor é o que abre uma partida
   * nova; nada mais abre.
   *
   * Existe porque o efeito não pode depender da identidade da função
   * `criarAdapter`. Ela é escrita como arrow function no ponto de uso, e
   * portanto nasce diferente a cada render do pai — inclusive no render
   * causado pela própria liquidação da partida. O resultado era a mesa
   * ser descartada e recriada no instante em que o jogo terminava: o
   * desfecho aparecia e sumia antes de alguém ler, e o tabuleiro voltava
   * do zero com o relógio correndo.
   */
  readonly chave: string;
  readonly onFinish: (result: MatchResult) => void;
  readonly onLeave: () => void;
}

function Relogio({ ms, ativo, nome }: { ms: number; ativo: boolean; nome: string }) {
  const acabando = ms < 30_000;
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-lg px-4 py-3 border transition"
      style={{
        background: ativo ? 'var(--accent)' : 'var(--muted)',
        borderColor: ativo ? 'var(--brass)' : 'transparent',
        color: ativo ? 'var(--accent-foreground)' : undefined,
      }}
    >
      <span className="text-sm font-medium truncate min-w-0">{nome}</span>
      <span
        className="font-mono text-lg font-bold tabular-nums"
        style={acabando ? { color: 'var(--danger)' } : undefined}
      >
        {clock(ms)}
      </span>
    </div>
  );
}

export function MatchScreen(props: MatchScreenProps) {
  const [snap, setSnap] = useState<MatchSnapshot | null>(null);
  const adapterRef = useRef<MatchAdapter | null>(null);
  const finishedRef = useRef(false);

  // A fábrica fica num ref para o efeito poder usá-la sem depender dela.
  // O ref é atualizado a cada render, então a versão usada ao abrir uma
  // partida nova é sempre a mais recente.
  const fabricaRef = useRef(props.criarAdapter);
  fabricaRef.current = props.criarAdapter;

  const { chave } = props;
  useEffect(() => {
    const adapter = fabricaRef.current();
    adapterRef.current = adapter;
    finishedRef.current = false;
    setSnap(null);

    const unsubscribe = adapter.subscribe(setSnap);
    return () => {
      unsubscribe();
      adapter.dispose();
      adapterRef.current = null;
    };
  }, [chave]);

  // A liquidacao acontece uma vez so, mesmo que o estado seja reemitido.
  useEffect(() => {
    if (!snap?.outcome || finishedRef.current) return;
    finishedRef.current = true;

    const empate = snap.outcome.kind === 'DRAW';
    const venceu = snap.outcome.kind === 'WIN' && snap.outcome.seat === snap.mySeat;
    props.onFinish({
      empate,
      venceu,
      creditoCents: empate ? props.entryFeeCents : venceu ? payoutCents(props.entryFeeCents) : 0,
    });
  }, [snap?.outcome, snap?.mySeat, props]);

  const desfecho = useMemo(() => {
    if (!snap?.outcome) return null;
    if (snap.outcome.kind === 'DRAW') {
      return {
        titulo: 'Empate',
        detalhe: `Por ${reason(snap.outcome.reason)}. Entrada devolvida, sem rake.`,
        tom: 'neutro' as const,
      };
    }
    const venceu = snap.outcome.seat === snap.mySeat;
    return {
      titulo: venceu ? 'Você venceu' : 'Você perdeu',
      detalhe: venceu
        ? `Por ${reason(snap.outcome.reason)}. Prêmio de ${money(payoutCents(props.entryFeeCents))}, já descontado o rake de ${money(rakeCents(props.entryFeeCents))}.`
        : `Por ${reason(snap.outcome.reason)}.`,
      tom: venceu ? ('bom' as const) : ('ruim' as const),
    };
  }, [snap?.outcome, snap?.mySeat, props.entryFeeCents]);

  // O que o aviso de fim de partida mostra. Separado de `desfecho` porque
  // aquele é texto para o painel, e este é dinheiro: a variação com sinal,
  // que é a única coisa que a pessoa precisa levar da tela.
  const overlay = useMemo((): {
    tipo: TipoDesfecho;
    motivo: string;
    variacaoCents: number;
    rakeCents: number;
  } | null => {
    if (!snap?.outcome) return null;

    const rake = rakeCents(props.entryFeeCents);
    if (snap.outcome.kind === 'DRAW') {
      return { tipo: 'empate', motivo: reason(snap.outcome.reason), variacaoCents: 0, rakeCents: 0 };
    }

    const venceu = snap.outcome.seat === snap.mySeat;
    return {
      tipo: venceu ? 'vitoria' : 'derrota',
      motivo: reason(snap.outcome.reason),
      // Ganho é o prêmio menos a própria entrada, que já saiu do saldo.
      // Mostrar o prêmio cheio como "ganho" contaria a entrada duas vezes.
      variacaoCents: venceu
        ? payoutCents(props.entryFeeCents) - props.entryFeeCents
        : -props.entryFeeCents,
      rakeCents: venceu ? rake : 0,
    };
  }, [snap?.outcome, snap?.mySeat, props.entryFeeCents]);

  if (!snap) return <EsqueletoMesa />;

  const minhaVez = snap.turn === snap.mySeat && !snap.outcome;

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 grid gap-6 lg:grid-cols-[1fr_20rem] items-start grow">
      <div className="min-w-0">
        <Board
          gameType={snap.gameType}
          state={snap.state}
          legalMoves={snap.legalMoves}
          myTurn={minhaVez}
          lastMove={snap.lastMove}
          onPlay={(move) => adapterRef.current?.play(move)}
        />
        {snap.error ? (
          <p role="alert" className="mt-3 text-sm font-medium" style={{ color: 'var(--danger)' }}>
            {snap.error}
          </p>
        ) : null}
      </div>

      <aside className="grid gap-4 content-start min-w-0">
        <div className="card card-destaque p-4 grid gap-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">{GAME_LABEL[snap.gameType]}</h2>
            <span className="selo">virtual</span>
          </div>

          <Relogio ms={snap.remainingMs[1] ?? 0} ativo={snap.turn === 1 && !snap.outcome} nome={snap.opponentName} />
          <Relogio ms={snap.remainingMs[0] ?? 0} ativo={minhaVez} nome="Você" />

          <div className="grid grid-cols-2 gap-2">
            <div className="stat">
              <div className="rotulo">Entrada</div>
              <div className="stat-value money">{money(props.entryFeeCents)}</div>
            </div>
            <div className="stat">
              <div className="rotulo">Prêmio</div>
              <div className="stat-value money">{money(payoutCents(props.entryFeeCents))}</div>
            </div>
          </div>

          <p
            className="text-sm font-medium"
            aria-live="polite"
            style={{ color: minhaVez ? 'var(--primary)' : 'var(--muted-foreground)' }}
          >
            {snap.outcome ? 'Partida encerrada.' : minhaVez ? 'Sua vez.' : `${snap.opponentName} está pensando…`}
          </p>
        </div>

        {desfecho ? (
          <div className="card p-4 grid gap-3">
            <h3
              className="text-lg font-bold"
              style={{
                color:
                  desfecho.tom === 'bom'
                    ? 'var(--primary)'
                    : desfecho.tom === 'ruim'
                      ? 'var(--danger)'
                      : undefined,
              }}
            >
              {desfecho.titulo}
            </h3>
            <p className="text-sm text-muted-foreground">{desfecho.detalhe}</p>
            <button type="button" className="btn btn-primary" onClick={props.onLeave}>
              Voltar ao lobby
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => adapterRef.current?.resign()}
          >
            Desistir
          </button>
        )}
      </aside>

      {/* O painel lateral continua trazendo o desfecho por escrito: o
          jogador que fecha o aviso precisa achar a informação em algum
          lugar, e não pode depender de ter lido a tempo. */}
      <AnimatePresence>
        {overlay ? (
          <Desfecho
            key="desfecho"
            tipo={overlay.tipo}
            motivo={overlay.motivo}
            variacaoCents={overlay.variacaoCents}
            rakeCents={overlay.rakeCents}
            adversario={props.adversario ?? null}
            {...(props.onJogarDeNovo ? { onJogarDeNovo: props.onJogarDeNovo } : {})}
            onSair={props.onLeave}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}
